import { TextField } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import debounce from "lodash/debounce";
import { useMemo, useEffect } from "react";
import { createUseStyles } from "react-jss";
import { useQuery, useQueryClient } from "react-query";
import LimitTag from "$components/LimitTag";
import useAuth from "$hooks/useAuth";
import { useList } from "$hooks/useList";
import useOrder from "$hooks/useOrder";
import { EntrepotService } from "$services";
import { OrderStatus } from "$types";
import { CacheInvalidationAction } from "$utils/cache";
import { theme } from "$utils/theme/bataillog";

// Define a proper type for your order items
export interface OrderItem {
  _id: string;
  reference: string;
  name?: string;
  company: string | { _id: string; name: string };
  createdAt: string;
  providerType?: string;
  inputValue?: string;
}

interface Company {
  _id: string;
  name: string;
  isActive: boolean;
}

// Update the filter options creation
const autoCompleteOptions = createFilterOptions<OrderItem>({
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
});

type Props = {
  companies: Company[];
  displayCreateOrderOption: boolean;
  label?: string;
  limitTags?: number;
  onChange: (values: OrderItem[], rows: OrderItem[]) => void;
  placeholder?: string;
  value: OrderItem[];
  isTransfer?: boolean;
  appendToDefaultFilters?: Record<string, any>;
  invalidateQuery?: boolean;
  excludeOrderReferences?: string[];
};

function OrderSelect({
  label,
  limitTags = 2,
  value,
  onChange,
  placeholder,
  companies,
  displayCreateOrderOption,
  isTransfer = false,
  appendToDefaultFilters = {},
  invalidateQuery = false,
  excludeOrderReferences = [],
}: Props) {
  const classes = useStyles();
  const { isContributor } = useAuth();
  const { filter: companySidebarFilter } = useOrder();
  const queryClient = useQueryClient();

  const { data: warehouses } = useQuery(
    [
      CacheInvalidationAction.GET_ENTREPOTS,
      { company: companySidebarFilter?._id },
    ],
    () =>
      EntrepotService.getEntrepots({
        company: companySidebarFilter?._id,
      }),
    {
      enabled: !!companySidebarFilter?._id,
      cacheTime: 5 * 1000 * 60,
      refetchOnWindowFocus: false,
    }
  ) as any;

  // Create default options for useList with proper format for the new implementation
  const defaultOptions = useMemo(
    () => ({
      ignoreSideBarFilter: isContributor(),
      defaultFilters: {
        "company][$in": companies
          .filter(({ isActive }) => isActive)
          .map(({ _id }) => _id),
        status$ne: OrderStatus.CANCELED,
        ...(isContributor()
          ? {
              warehouse: { $in: warehouses?.data?.map(({ _id }) => _id) ?? [] },
            }
          : {}),
        ...appendToDefaultFilters,
      },
      defaultSort: "orderDate:desc",
      defaultLimit: 20,
      populate: [
        "suborders",
        "suborders.suborderProducts",
        "receptions",
        "files",
      ],
      ready: true,
      cachePrefix: "orderselect",
    }),
    [appendToDefaultFilters, companies, isContributor, warehouses?.data]
  );

  // Use the new useList hook
  const {
    data: items,
    setFilter: setOrderFilter,
    setData: setItems,
    filters,
  } = useList<any>(
    "api/order/orders/consolidated", // Use the consolidated endpoint
    defaultOptions
  );

  // Invalidate query when invalidateQuery is true
  useEffect(() => {
    if (invalidateQuery) {
      queryClient.invalidateQueries({
        queryKey: ["api/order/orders/consolidated"],
        exact: false,
      });
    }
  }, [invalidateQuery, queryClient]);

  const orderList = useMemo(() => {
    const data = items as any;

    if (!data) return [];

    if (Array.isArray(data)) {
      return data;
    }

    if (typeof data === "object" && "data" in data) {
      return data.data || [];
    }

    return [];
  }, [items]);

  // Filter and sort orders
  const orderOptions = useMemo(() => {
    let orders = orderList || [];
    if ((!isContributor && companySidebarFilter) || filters?.company) {
      orders = orders.filter((order: OrderItem) => {
        const orderCompanyId =
          typeof order.company === "object" ? order.company._id : order.company;

        return (
          orderCompanyId === (companySidebarFilter?._id || filters?.company)
        );
      });
    }

    // Filter out already selected orders in the current reception
    if (excludeOrderReferences.length > 0) {
      orders = orders.filter(
        (order: OrderItem) => !excludeOrderReferences.includes(order.reference)
      );
    }

    return orders.sort(
      (o1: OrderItem, o2: OrderItem) =>
        new Date(o2.createdAt).getTime() - new Date(o1.createdAt).getTime()
    );
  }, [
    orderList,
    filters,
    companySidebarFilter,
    isContributor,
    excludeOrderReferences,
  ]);

  // Debounced search handler
  const handleSearchOrder = useMemo(
    () =>
      debounce((e, val) => {
        if (val !== "") {
          // Use the new filter format
          setOrderFilter("reference", { $containsi: val });
        } else {
          setItems([]);
          setOrderFilter("reference", { $containsi: val });
        }
      }, 500),
    [setItems, setOrderFilter]
  );

  return (
    <Autocomplete
      onInputChange={(e, val) => {
        handleSearchOrder(e, val);
      }}
      disableClearable
      className={classes.select}
      limitTags={limitTags}
      getLimitTagsText={(more) => <LimitTag nbMore={more} />}
      isOptionEqualToValue={(option, value) =>
        option?.reference === value.reference
      }
      options={(orderOptions as OrderItem[]) ?? []}
      onChange={(event, newValues) => {
        const tempOrders = ((orderList as OrderItem[]) || []).filter((order) =>
          newValues.some((newValue) => {
            const orderRef = order.reference;
            const newValueRef = newValue.reference || newValue.inputValue;
            const orderCompany =
              typeof order.company === "object"
                ? order.company._id
                : order.company;
            const newValueCompany = newValue.company;

            return (
              orderRef === newValueRef &&
              (orderCompany === newValueCompany || !newValueCompany)
            );
          })
        );

        onChange(newValues, tempOrders);
      }}
      value={value}
      filterOptions={(options, params) => {
        // Apply the base filter first
        let filtered = autoCompleteOptions(options, params);

        // Then apply your custom filters
        if (isTransfer) {
          filtered = filtered.filter(
            (elem) => elem.providerType === "transfer"
          );
        } else {
          filtered = filtered.filter(
            (elem) => elem.providerType !== "transfer"
          );
        }

        const { inputValue } = params;

        // Add the "Create order" option
        if (displayCreateOrderOption) {
          const doesExist = options.some(
            (option) => inputValue === option.reference
          );
          if (!doesExist) {
            const createOrderText = inputValue
              ? `Créer commande "${inputValue}"`
              : "Créer commande";

            filtered.push({
              _id: "new",
              inputValue: inputValue || "",
              name: createOrderText,
              reference: inputValue || "",
              company: "",
              createdAt: new Date().toISOString(),
            });
          }
        }

        return filtered;
      }}
      multiple
      getOptionLabel={(option) => {
        // If option is just a string (reference)
        if (typeof option === "string") {
          return option;
        }

        // Handle the "Create order" option
        if (option._id === "new") {
          return option.inputValue
            ? `Créer commande "${option.inputValue}"`
            : "Créer commande";
        }

        // Get company information
        const companyId =
          typeof option.company === "object"
            ? option.company._id
            : option.company;

        const comp = companies.find((company) => company._id === companyId);

        return comp
          ? `${option.reference} [${comp.name}]`
          : option.name ?? option.reference ?? "";
      }}
      renderOption={(props, option) => {
        // Handle the "Create order" option
        if (option._id === "new") {
          const displayText = option.inputValue
            ? `Créer commande "${option.inputValue}"`
            : "Créer commande";

          return (
            <li {...props} key="create-new">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {displayText}
              </div>
            </li>
          );
        }

        const companyId =
          typeof option.company === "object"
            ? option.company._id
            : option.company;

        const comp = companies.find((company) => company._id === companyId);

        return (
          <li {...props} key={option._id || option.reference}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {option.reference ?? option.name}
              {comp && (
                <div
                  style={{
                    color: "grey",
                    fontSize: "0.8em",
                  }}
                >{`${comp.name}`}</div>
              )}
            </div>
          </li>
        );
      }}
      noOptionsText=""
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          style={{ width: "100%" }}
        />
      )}
    />
  );
}

const useStyles = createUseStyles(() => ({
  select: {
    flex: "1",
    paddingRight: "20px",
    "&:last-child": {
      padding: 0,
    },
    background: theme.color.white,
    "& .MuiInputLabel-shrink": {
      background: `${theme.color.white} !important`,
    },
  },
}));

export default OrderSelect;
